import { Platform } from 'react-native';
import { PHONE_INVALID_MESSAGE } from '@tabbio/utils';
import { APP_VERSION } from './app-version';
import {
  sanitizeObservabilityProperties,
  type ObservabilityProperties,
} from './observability-properties';
import { shouldDropExpectedClientError } from './sentry-error-filter';

const SENTRY_RELEASE = `com.tabbio.app@${APP_VERSION}`;

type SentryModule = typeof import('@sentry/react-native');

let sentryInitialized = false;
let sentryModule: SentryModule | null = null;
let sentryLoadPromise: Promise<SentryModule | null> | null = null;
let pendingUserContext:
  | {
      userId: string;
      role?: string | null;
      locale?: string | null;
      companyId?: string | null;
    }
  | null = null;
let pendingRouteContext:
  | {
      pathname: string;
      properties?: ObservabilityProperties;
    }
  | null = null;

function truncateTagValue(value: unknown) {
  const text = String(value);
  return text.length > 200 ? text.slice(0, 200) : text;
}

function getSentryModule() {
  if (sentryModule) {
    return Promise.resolve(sentryModule);
  }

  if (!sentryLoadPromise) {
    sentryLoadPromise = import('@sentry/react-native')
      .then((module) => {
        sentryModule = module;
        return module;
      })
      .catch(() => null);
  }

  return sentryLoadPromise;
}

function applyUserContext(
  Sentry: SentryModule,
  params: NonNullable<typeof pendingUserContext>,
) {
  Sentry.setUser({ id: params.userId });
  Sentry.setTag('surface', Platform.OS);

  if (params.role) {
    Sentry.setTag('user.role', truncateTagValue(params.role));
  }

  if (params.locale) {
    Sentry.setTag('user.locale', truncateTagValue(params.locale));
  }

  if (params.companyId) {
    Sentry.setTag('company.id', truncateTagValue(params.companyId));
  }

  Sentry.setContext(
    'user_context',
    sanitizeObservabilityProperties({
      role: params.role,
      locale: params.locale,
      companyId: params.companyId,
    }),
  );
}

function applyRouteContext(
  Sentry: SentryModule,
  pathname: string,
  properties?: ObservabilityProperties,
) {
  Sentry.setTag('surface', Platform.OS);
  Sentry.setTag('route', truncateTagValue(pathname));
  Sentry.setContext(
    'route',
    sanitizeObservabilityProperties({
      pathname,
      ...properties,
    }),
  );
}

export function initializeSentry() {
  const dsn = process.env.EXPO_PUBLIC_SENTRY_DSN;

  if (!dsn || sentryInitialized) {
    return;
  }

  void getSentryModule().then((Sentry) => {
    if (!Sentry || sentryInitialized) {
      return;
    }

    Sentry.init({
      dsn,
      release: SENTRY_RELEASE,
      environment:
        process.env.EXPO_PUBLIC_SENTRY_ENVIRONMENT ||
        process.env.EXPO_PUBLIC_POSTHOG_ENVIRONMENT ||
        (__DEV__ ? 'development' : 'production'),
      sendDefaultPii: false,
      tracesSampleRate: __DEV__ ? 1 : 0.1,
      ignoreErrors: [
        // Safari content-blocker extension global, not our code.
        /SCDynimacBridge/i,
        PHONE_INVALID_MESSAGE,
      ],
      beforeSend(event) {
        return shouldDropExpectedClientError(event) ? null : event;
      },
    });

    sentryInitialized = true;

    if (pendingUserContext) {
      applyUserContext(Sentry, pendingUserContext);
    }

    if (pendingRouteContext) {
      applyRouteContext(
        Sentry,
        pendingRouteContext.pathname,
        pendingRouteContext.properties,
      );
    }
  });
}

export function setSentryUserContext(params: {
  userId: string;
  role?: string | null;
  locale?: string | null;
  companyId?: string | null;
}) {
  pendingUserContext = params;

  if (!sentryInitialized || !sentryModule) {
    return;
  }

  applyUserContext(sentryModule, params);
}

export function clearSentryUserContext() {
  pendingUserContext = null;
  pendingRouteContext = null;

  if (!sentryInitialized || !sentryModule) {
    return;
  }

  sentryModule.setUser(null);
  sentryModule.setContext('user_context', null);
  sentryModule.setContext('route', null);
}

export function setSentryRouteContext(
  pathname: string,
  properties?: ObservabilityProperties,
) {
  pendingRouteContext = { pathname, properties };

  if (!sentryInitialized || !sentryModule) {
    return;
  }

  applyRouteContext(sentryModule, pathname, properties);
}

export function addSentryBreadcrumb(
  message: string,
  properties?: ObservabilityProperties,
) {
  if (!sentryInitialized || !sentryModule) {
    return;
  }

  sentryModule.addBreadcrumb({
    category: 'product',
    level: 'info',
    message,
    data: sanitizeObservabilityProperties(properties),
  });
}

export function captureSentryException(
  exception: unknown,
  properties?: ObservabilityProperties,
) {
  if (!sentryInitialized || !sentryModule) {
    return;
  }

  const sanitized = sanitizeObservabilityProperties(properties);

  sentryModule.withScope((scope) => {
    scope.setTag('surface', Platform.OS);

    for (const [key, value] of Object.entries(sanitized)) {
      if (
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean'
      ) {
        scope.setTag(key.slice(0, 32), truncateTagValue(value));
      }
    }

    scope.setContext('product', sanitized);
    sentryModule?.captureException(
      exception instanceof Error ? exception : new Error(String(exception)),
    );
  });
}

export function wrapRootWithSentry<Component>(component: Component): Component {
  return component;
}
